import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Mail, 
  Send, 
  Users, 
  Eye, 
  Download,
  Trash2,
  Plus,
  Search,
  Filter,
  X,
  Calendar,
  TrendingUp
} from 'lucide-react';

interface Subscriber {
  id: string;
  email: string;
  name?: string;
  subscribed: boolean;
  subscribedAt: string;
  unsubscribedAt?: string;
  source: string;
}

interface Campaign {
  id: string;
  subject: string;
  content: string;
  sentAt?: string;
  recipientCount: number;
  openCount: number;
  clickCount: number;
  status: 'draft' | 'scheduled' | 'sent';
  scheduledAt?: string;
}

interface CampaignFormData {
  subject: string;
  content: string;
  scheduledAt?: string;
}

const campaignSchema = yup.object({
  subject: yup
    .string()
    .required('Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject cannot exceed 100 characters'),
  
  content: yup
    .string()
    .required('Content is required')
    .min(50, 'Content must be at least 50 characters'),
  
  scheduledAt: yup
    .string()
    .optional()
});

const NewsletterManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'subscribers' | 'campaigns'>('subscribers');
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'subscribed' | 'unsubscribed'>('all');
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(campaignSchema)
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch subscribers and campaigns in parallel
      const [subscribersRes, campaignsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/newsletter/subscribers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/newsletter/campaigns`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (subscribersRes.ok && campaignsRes.ok) {
        const subscribersData = await subscribersRes.json();
        const campaignsData = await campaignsRes.json();
        
        setSubscribers(subscribersData.data?.subscribers || []);
        setCampaigns(campaignsData.data?.campaigns || []);
      } else {
        // Set sample data for demonstration
        setSubscribers([
          {
            id: '1',
            email: 'john.doe@example.com',
            name: 'John Doe',
            subscribed: true,
            subscribedAt: '2024-01-15T10:30:00Z',
            source: 'website'
          },
          {
            id: '2',
            email: 'jane.smith@example.com',
            name: 'Jane Smith',
            subscribed: true,
            subscribedAt: '2024-01-20T14:15:00Z',
            source: 'contact-form'
          },
          {
            id: '3',
            email: 'bob.wilson@example.com',
            subscribed: false,
            subscribedAt: '2024-01-10T09:00:00Z',
            unsubscribedAt: '2024-02-01T16:45:00Z',
            source: 'website'
          }
        ]);
        
        setCampaigns([
          {
            id: '1',
            subject: 'Welcome to Our Newsletter!',
            content: 'Thank you for subscribing to our newsletter...',
            sentAt: '2024-02-01T12:00:00Z',
            recipientCount: 150,
            openCount: 95,
            clickCount: 23,
            status: 'sent'
          },
          {
            id: '2',
            subject: 'Latest Web Design Trends 2024',
            content: 'Discover the latest trends in web design...',
            recipientCount: 0,
            openCount: 0,
            clickCount: 0,
            status: 'draft'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching newsletter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscribers = subscribers.filter(subscriber => {
    const matchesSearch = subscriber.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscriber.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'subscribed' && subscriber.subscribed) ||
                         (filterStatus === 'unsubscribed' && !subscriber.subscribed);
    
    return matchesSearch && matchesFilter;
  });

  const stats = {
    totalSubscribers: subscribers.length,
    activeSubscribers: subscribers.filter(s => s.subscribed).length,
    unsubscribed: subscribers.filter(s => !s.subscribed).length,
    campaignsSent: campaigns.filter(c => c.status === 'sent').length,
    averageOpenRate: campaigns.length > 0 
      ? Math.round(campaigns.reduce((acc, c) => acc + (c.recipientCount > 0 ? (c.openCount / c.recipientCount) * 100 : 0), 0) / campaigns.length)
      : 0
  };

  const onSubmitCampaign = async (data: any) => {
    try {
      const token = localStorage.getItem('adminToken');
      const campaignData = {
        ...data,
        scheduledAt: data.scheduledAt ? new Date(data.scheduledAt).toISOString() : undefined
      };

      const url = editingCampaign
        ? `${process.env.REACT_APP_API_URL}/admin/newsletter/campaigns/${editingCampaign.id}`
        : `${process.env.REACT_APP_API_URL}/admin/newsletter/campaigns`;
      
      const method = editingCampaign ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(campaignData),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingCampaign ? 'update' : 'create'} campaign`);
      }

      alert(`Campaign ${editingCampaign ? 'updated' : 'created'} successfully!`);
      setShowCampaignForm(false);
      setEditingCampaign(null);
      reset();
      fetchData();

    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign');
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to send this campaign to all subscribers?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/newsletter/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to send campaign');
      }

      alert('Campaign sent successfully!');
      fetchData();

    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/newsletter/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete campaign');
      }

      alert('Campaign deleted successfully!');
      fetchData();

    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    reset({
      subject: campaign.subject,
      content: campaign.content,
      scheduledAt: campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().slice(0, 16) : ''
    });
    setShowCampaignForm(true);
  };

  const exportSubscribers = () => {
    const csvContent = [
      ['Email', 'Name', 'Status', 'Subscribed Date', 'Source'].join(','),
      ...filteredSubscribers.map(sub => [
        sub.email,
        sub.name || '',
        sub.subscribed ? 'Active' : 'Unsubscribed',
        new Date(sub.subscribedAt).toLocaleDateString(),
        sub.source
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter-subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setShowCampaignForm(false);
    setEditingCampaign(null);
    reset();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Newsletter Management</h1>
          <p className="text-gray-600">Manage subscribers and email campaigns</p>
        </div>
        <button
          onClick={() => setShowCampaignForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Subscribers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSubscribers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSubscribers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Mail className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Campaigns Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.campaignsSent}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Eye className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Open Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageOpenRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unsubscribed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.unsubscribed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'subscribers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Subscribers ({subscribers.length})
          </button>
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'campaigns'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Campaigns ({campaigns.length})
          </button>
        </nav>
      </div>

      {/* Campaign Form Modal */}
      {showCampaignForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmitCampaign)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  {...register('subject')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter campaign subject..."
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  {...register('content')}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Write your campaign content here..."
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Send (Optional)
                </label>
                <input
                  {...register('scheduledAt')}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty to save as draft
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'subscribers' ? (
        <div className="bg-white shadow rounded-lg">
          {/* Subscribers Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Subscribers</h2>
              <button
                onClick={exportSubscribers}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>

            {/* Search and Filter */}
            <div className="mt-4 flex space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search subscribers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="subscribed">Active</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>
          </div>

          {/* Subscribers List */}
          <div className="divide-y divide-gray-200">
            {filteredSubscribers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No subscribers found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || filterStatus !== 'all' 
                    ? 'Try adjusting your search or filter.'
                    : 'Subscribers will appear here once people sign up for your newsletter.'
                  }
                </p>
              </div>
            ) : (
              filteredSubscribers.map((subscriber) => (
                <div key={subscriber.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {subscriber.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-600">{subscriber.email}</p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            subscriber.subscribed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {subscriber.subscribed ? 'Active' : 'Unsubscribed'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>Joined: {new Date(subscriber.subscribedAt).toLocaleDateString()}</span>
                        <span>Source: {subscriber.source}</span>
                        {!subscriber.subscribed && subscriber.unsubscribedAt && (
                          <span>Unsubscribed: {new Date(subscriber.unsubscribedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          {/* Campaigns Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Email Campaigns</h2>
          </div>

          {/* Campaigns List */}
          <div className="divide-y divide-gray-200">
            {campaigns.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns yet</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating your first email campaign.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowCampaignForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Campaign
                  </button>
                </div>
              </div>
            ) : (
              campaigns.map((campaign) => (
                <div key={campaign.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{campaign.subject}</h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            campaign.status === 'sent'
                              ? 'bg-green-100 text-green-800'
                              : campaign.status === 'scheduled'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                        </span>
                      </div>

                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {campaign.content.substring(0, 150)}...
                      </p>

                      <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                        {campaign.sentAt && (
                          <span>Sent: {new Date(campaign.sentAt).toLocaleDateString()}</span>
                        )}
                        {campaign.scheduledAt && (
                          <span>Scheduled: {new Date(campaign.scheduledAt).toLocaleDateString()}</span>
                        )}
                        {campaign.status === 'sent' && (
                          <>
                            <span>{campaign.recipientCount} recipients</span>
                            <span>{campaign.openCount} opens ({Math.round((campaign.openCount / campaign.recipientCount) * 100)}%)</span>
                            <span>{campaign.clickCount} clicks</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {campaign.status === 'draft' && (
                        <button
                          onClick={() => handleSendCampaign(campaign.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send Now
                        </button>
                      )}

                      <button
                        onClick={() => handleEditCampaign(campaign)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                        title="Edit Campaign"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete Campaign"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;